export type Principal = {
  sub?: string;
  user_id?: string;
  email?: string;
  tenant_id: string;
  role: string;
};

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';

export function getAccessToken(): string | null {
  return sessionStorage.getItem('eduversa.access_token');
}

export function setAccessToken(token: string | null): void {
  if (token) sessionStorage.setItem('eduversa.access_token', token);
  else sessionStorage.removeItem('eduversa.access_token');
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const message = typeof payload === 'object' && payload && 'detail' in payload
      ? String((payload as any).detail)
      : `HTTP ${response.status}`;
    throw new ApiError(response.status, message, payload);
  }
  return payload as T;
}

export async function login(email: string, password: string): Promise<string> {
  const result = await apiRequest<{ access_token: string }>('/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  return result.access_token;
}

export function fetchMe(): Promise<Principal> {
  return apiRequest<Principal>('/me');
}

export function fetchHealth(): Promise<{ status: string; version: string }> {
  return apiRequest<{ status: string; version: string }>('/health');
}


export type Course = { id:string; tenant_id:string; code:string; title:string; description:string };
export type Program = { id:string; tenant_id:string; level_id:string; code:string; title:string };
export type Entitlement = { id?:string; product_id?:string; user_id?:string; status?:string; [key:string]:unknown };
export type Enrollment = { id:string; offering_id:string; student_id:string; status:string };
export type SyllabusNode = { id:string; parent_id:string|null; kind:string; title:string; position:number };
export type Artifact = { id:string; kind:string; version:number; title:string; status:string };
export type ProgressSummary = { enrollment_id:string; percentage:number|null; credits_earned:number; completed:boolean };
export type BangesCandidate = { id:string; subject_ref:string; evidence:string; status:string; review_note:string };
export type BangesRecommendation = { id:string; recommendation_type:string; title:string; target_ref:string; rationale:string; status:string };

/** Registre académique central : 943 cours de référence, tous établissements. */
export const REGISTRY_TENANT_ID = 'b282f340-4437-4585-acc6-a9fda3f90d1d';

export type AcademicFieldRef = { id: string; code: string; title: string; description: string };

export type CatalogCourse = {
  id: string;
  tenant_id: string;
  code: string;
  title: string;
  description: string;
  domain: string | null;
  domain_field_id: string | null;
  level_code: string | null;
};

export type CatalogPage = {
  total: number;
  limit: number;
  offset: number;
  items: CatalogCourse[];
};

/** Catalogue filtrable. /courses renvoie {total, items} depuis C46. */
export const catalogApi = {
  fields: (tenantId?: string) =>
    apiRequest<AcademicFieldRef[]>(
      tenantId ? `/mar/fields?tenant_id=${encodeURIComponent(tenantId)}` : '/mar/fields',
    ),
  courses: (opts: {
    tenantId?: string;
    q?: string;
    level?: string;
    fieldId?: string;
    limit?: number;
    offset?: number;
  } = {}) => {
    const p = new URLSearchParams();
    if (opts.tenantId) p.set('tenant_id', opts.tenantId);
    if (opts.q) p.set('q', opts.q);
    if (opts.level) p.set('level', opts.level);
    if (opts.fieldId) p.set('field_id', opts.fieldId);
    if (opts.limit != null) p.set('limit', String(opts.limit));
    if (opts.offset != null) p.set('offset', String(opts.offset));
    const qs = p.toString();
    return apiRequest<CatalogPage>(qs ? `/courses?${qs}` : '/courses');
  },
};

export const studentApi = {
  courses: (tenantId?: string) => apiRequest<Course[]>(tenantId ? `/courses?tenant_id=${encodeURIComponent(tenantId)}` : '/courses'),
  programs: () => apiRequest<Program[]>('/programs'),
  entitlements: () => apiRequest<Entitlement[]>('/entitlements'),
  myEnrollments: () => apiRequest<Enrollment[]>('/my/enrollments'),
  syllabus: (courseVersionId:string) => apiRequest<{id:string;title:string;description:string;status:string}|null>(`/course-versions/${courseVersionId}/syllabus`),
  syllabusNodes: (syllabusId:string) => apiRequest<SyllabusNode[]>(`/syllabi/${syllabusId}/nodes`),
  artifacts: (nodeId:string) => apiRequest<Artifact[]>(`/syllabus-nodes/${nodeId}/artifacts`),
  artifact: (artifactId:string) => apiRequest<{id:string;kind:string;title:string;body:string;status:string}>(`/content-artifacts/${artifactId}`),
  progress: (enrollmentId:string) => apiRequest<ProgressSummary>(`/enrollments/${enrollmentId}/progress`),
  createAiSession: (mode:'PROFESSOR'|'COACH', courseVersionId?:string) => apiRequest<{id:string;mode:string;status:string;course_version_id?:string}>('/ai/sessions',{method:'POST',body:JSON.stringify({mode,course_version_id:courseVersionId||null})}),
  aiMessage: (sessionId:string,text:string) => apiRequest<{answer:string;source_ids:string[];provider:string;model:string}>(`/ai/sessions/${sessionId}/messages`,{method:'POST',body:JSON.stringify({text})}),
  bangesCandidates: (learnerId:string) => apiRequest<BangesCandidate[]>(`/banges/learners/${learnerId}/candidates`),
  bangesRecommendations: (learnerId:string) => apiRequest<BangesRecommendation[]>(`/banges/learners/${learnerId}/recommendations`),
};


export type LiveSession = { id:string;room_id:string;room_code:string;room_title:string;course_version_id:string|null;status:string;scheduled_at:string|null;started_at:string|null;ended_at:string|null;role:string;joined_at:string|null };
export type WebRtcConfig = { ice_servers:Array<{urls:string|string[];username?:string;credential?:string}>;sfu:{enabled:boolean;url:string|null};signaling:{transport:string;events:string[]} };
export type Laboratory = { id:string;version_id:string;code:string;title:string;family:string;requirement_type:string;version:number;definition:Record<string,unknown> };
export type LabSession = { id:string;laboratory_version_id:string;status:string;state:Record<string,unknown>;current_checkpoint:string|null;completed_at:string|null;created_at?:string;measurements?:Array<{id:string;name:string;value:number;unit:string;created_at:string}>;report?:{id:string;content:string;status:string}|null };
export type Recording = { id:string;session_id:string;status:string;storage_ref:string|null;export_ref:string|null;retention_until:string|null;source_kind:string;retention_paid_until:string|null;warning_sent_at:string|null;created_at:string };
export type TranslationTrack = { id:string;source_language:string;target_language:string;status:string;transcript_text:string|null;translated_text:string|null };

export function websocketUrl(roomId:string):string {
  const token=getAccessToken() || '';
  const protocol=window.location.protocol==='https:'?'wss:':'ws:';
  const host=(import.meta as any).env?.VITE_WS_HOST || window.location.host;
  return `${protocol}//${host}/api/v1/realtime/rooms/${encodeURIComponent(roomId)}?token=${encodeURIComponent(token)}`;
}

export const realtimeApi = {
  sessions:()=>apiRequest<LiveSession[]>('/live/my-sessions'),
  join:(sessionId:string)=>apiRequest<{session_id:string;user_id:string;role:string;joined_at:string}>(`/live/sessions/${sessionId}/join`,{method:'POST'}),
  participants:(sessionId:string)=>apiRequest<Array<{user_id:string;role:string;joined_at:string|null}>>(`/live/sessions/${sessionId}/participants`),
  webrtc:()=>apiRequest<WebRtcConfig>('/webrtc/config'),
  createStudentRecording:(sessionId:string,sourceKind:string)=>apiRequest<Recording & {charge_minor:number;currency:string}>(`/media/student/sessions/${sessionId}/recordings`,{method:'POST',headers:{'Idempotency-Key':crypto.randomUUID()},body:JSON.stringify({source_kind:sourceKind})}),
  translations:(sessionId:string)=>apiRequest<TranslationTrack[]>(`/media/sessions/${sessionId}/translations`),
};

export type ClassroomScene = { code:string;label:string;description:string;has_board:boolean;has_desk:boolean;seats:string[] };
export type ClassroomPresenter = { id:string;kind:'AI_AVATAR'|'HUMAN';user_id:string|null;display_name:string;avatar_ref:string;seat:string;is_speaking:boolean };
export type ClassroomDisplay = { id:string;kind:string;title:string;media_ref:string;presenter_id:string|null };
export type ClassroomView = { session_id:string;room_id:string;teaching_mode:'AI_ONLY'|'HUMAN'|'HYBRID';scene_preset:string;has_board:boolean;has_desk:boolean;board_label:string;assistant_per_learner:boolean;presenters:ClassroomPresenter[];display:ClassroomDisplay|null;podium:{id:string;user_id:string;display_name:string;topic:string;status:string}|null;pending_hands:number };

export const classroomApi = {
  scenes:()=>apiRequest<ClassroomScene[]>('/classroom/scenes'),
  setup:(roomId:string)=>apiRequest<any>(`/classroom/rooms/${roomId}/setup`),
  configure:(roomId:string,body:Record<string,unknown>)=>apiRequest<any>(`/classroom/rooms/${roomId}/setup`,{method:'PUT',body:JSON.stringify(body)}),
  stage:(sessionId:string,humanUserId?:string)=>apiRequest<ClassroomPresenter[]>(`/classroom/sessions/${sessionId}/stage`,{method:'POST',body:JSON.stringify({human_user_id:humanUserId||null})}),
  view:(sessionId:string)=>apiRequest<ClassroomView>(`/classroom/sessions/${sessionId}/view`),
  takeFloor:(presenterId:string)=>apiRequest<ClassroomPresenter>(`/classroom/presenters/${presenterId}/take-floor`,{method:'POST'}),
  releaseFloor:(presenterId:string)=>apiRequest<ClassroomPresenter>(`/classroom/presenters/${presenterId}/release-floor`,{method:'POST'}),
  present:(sessionId:string,body:Record<string,unknown>)=>apiRequest<ClassroomDisplay>(`/classroom/sessions/${sessionId}/present`,{method:'POST',body:JSON.stringify(body)}),
  clear:(sessionId:string)=>apiRequest<unknown>(`/classroom/sessions/${sessionId}/present`,{method:'DELETE'}),
};

export type BookPrice = { item_kind:string;fulfilment:string;amount_minor:number;currency:string };
export type BookOrder = { id:string;item_kind:string;item_title:string;fulfilment:string;amount_minor:number;currency:string;status:string;kdp_queue_id:string|null;created_at:string };
export type SalesOrder = BookOrder & { buyer_first_name:string;buyer_last_name:string;country:string;address_line:string;city:string;postal_code:string;phone:string;assigned_admin_user_id:string|null };
export type PaymentProvider = { id:string;code:string;kind:string;label:string;mode:string;country_code:string;currency:string;supports_payin:boolean;supports_payout:boolean;webhook_secret_configured:boolean };
export type PaymentIntent = { id:string;amount_minor:number;currency:string;purpose:string;reference_id:string;status:string;checkout_url:string;confirmed_at:string|null };
export type HandRaise = { id:string;session_id:string;user_id:string;display_name:string;topic:string;status:string;speaking_seconds:number };

/** Tenant du pilote. À rendre configurable dès qu'un second établissement arrive. */
export const PILOT_TENANT_ID = '1f2cb4d7-2483-4e15-a160-6085d4855584';

export const authApi = {
  signup:(email:string,password:string)=>apiRequest<{id:string;email:string;email_verified:boolean}>('/users',{method:'POST',body:JSON.stringify({email,password,tenant_id:PILOT_TENANT_ID,role:'learner'})}),
  confirmEmail:(token:string)=>apiRequest<{verified:boolean;email:string}>('/auth/verify-email/confirm',{method:'POST',body:JSON.stringify({token})}),
  resendVerification:(email:string)=>apiRequest<{sent:boolean}>('/auth/verify-email/resend',{method:'POST',body:JSON.stringify({email})}),
  logout:()=>apiRequest<{revoked:boolean}>('/logout',{method:'POST'}),
  sessions:()=>apiRequest<Array<{id:string;current:boolean;revoked:boolean;user_agent:string;ip_address:string;created_at:string}>>('/me/sessions'),
  requestReset:(email:string)=>apiRequest<{sent:boolean}>('/auth/password-reset/request',{method:'POST',body:JSON.stringify({email})}),
  confirmReset:(token:string,newPassword:string)=>apiRequest<{updated:boolean}>('/auth/password-reset/confirm',{method:'POST',body:JSON.stringify({token,new_password:newPassword})}),
};

export const operationsApi = {
  fees:()=>apiRequest<Array<{code:string;amount_minor:number;currency:string;is_default:boolean;default_minor:number}>>('/platform-fees'),
  setFee:(code:string,amountMinor:number)=>apiRequest<unknown>('/platform-fees',{method:'PUT',body:JSON.stringify({code,amount_minor:amountMinor})}),
  outbox:()=>apiRequest<Array<{id:string;to_address:string;subject:string;kind:string;status:string;error:string;body:string;created_at:string}>>('/outbox'),
};

export const academicApi = {
  levels:()=>apiRequest<any[]>('/academic-levels'),
  createLevel:(b:Record<string,unknown>)=>apiRequest<any>('/academic-levels',{method:'POST',body:JSON.stringify(b)}),
  courses:()=>apiRequest<any[]>('/courses'),
  createCourse:(b:Record<string,unknown>)=>apiRequest<any>('/courses',{method:'POST',body:JSON.stringify(b)}),
  versions:(courseId:string)=>apiRequest<any[]>(`/courses/${courseId}/versions`),
  createVersion:(courseId:string,b:Record<string,unknown>)=>apiRequest<any>(`/courses/${courseId}/versions`,{method:'POST',body:JSON.stringify(b)}),
  prerequisites:(cvId:string)=>apiRequest<any[]>(`/course-versions/${cvId}/prerequisites`),
  addPrerequisite:(cvId:string,b:Record<string,unknown>)=>apiRequest<any>(`/course-versions/${cvId}/prerequisites`,{method:'POST',body:JSON.stringify(b)}),
  syllabi:(cvId:string)=>apiRequest<any[]>(`/course-versions/${cvId}/syllabi`),
  createSyllabus:(cvId:string,b:Record<string,unknown>)=>apiRequest<any>(`/course-versions/${cvId}/syllabi`,{method:'POST',body:JSON.stringify(b)}),
  publishSyllabus:(id:string)=>apiRequest<any>(`/syllabi/${id}/publish`,{method:'POST'}),
  programs:()=>apiRequest<any[]>('/programs'),
  createProgram:(b:Record<string,unknown>)=>apiRequest<any>('/programs',{method:'POST',body:JSON.stringify(b)}),
  programVersions:(pid:string)=>apiRequest<any[]>(`/programs/${pid}/versions`),
  createProgramVersion:(pid:string,b:Record<string,unknown>)=>apiRequest<any>(`/programs/${pid}/versions`,{method:'POST',body:JSON.stringify(b)}),
  requirements:(pvid:string)=>apiRequest<any[]>(`/program-versions/${pvid}/requirements`),
  addRequirement:(pvid:string,b:Record<string,unknown>)=>apiRequest<any>(`/program-versions/${pvid}/requirements`,{method:'POST',body:JSON.stringify(b)}),
  degreeAudit:(pvid:string,studentId:string)=>apiRequest<any>(`/program-versions/${pvid}/degree-audit/${studentId}`),
};

export const publishingApi = {
  theory:(id:string)=>apiRequest<any>(`/publishing/module-theories/${id}`),
  workbook:(id:string)=>apiRequest<any>(`/publishing/module-workbooks/${id}`),
  publication:(id:string)=>apiRequest<any>(`/publishing/course-publications/${id}`),
  generate:(syllabusId:string,language='fr')=>apiRequest<any>(`/publishing/syllabi/${syllabusId}/generate`,{method:'POST',body:JSON.stringify({language})}),
  packageSyllabus:(syllabusId:string)=>apiRequest<any>(`/publishing/syllabi/${syllabusId}/package`),
  registry:(b:Record<string,unknown>)=>apiRequest<any>('/publishing/generate-registry',{method:'POST',body:JSON.stringify(b)}),
  normalize:()=>apiRequest<any>('/publishing/normalize-missing-modules',{method:'POST'}),
  freeze:(offeringId:string)=>apiRequest<any>(`/publishing/offerings/${offeringId}/freeze-syllabus`,{method:'POST'}),
  baseEditions:(pubId:string)=>apiRequest<any>(`/publishing/course-publications/${pubId}/base-language-editions`,{method:'POST'}),
  addEdition:(pubId:string,language:string)=>apiRequest<any>(`/publishing/course-publications/${pubId}/language-editions`,{method:'POST',body:JSON.stringify({language})}),
  setAccessPrice:(kind:string,amountMinor:number)=>apiRequest<any>(`/publishing/access-prices/${kind}`,{method:'POST',body:JSON.stringify({amount_minor:amountMinor,currency:'USD'})}),
  purchaseAccess:(kind:string,contentId:string)=>apiRequest<any>(`/publishing/${kind}/${contentId}/purchase-access`,{method:'POST',body:JSON.stringify({payment_reference:''})}),
};

export const mediaAdminApi = {
  sessionRecordings:(sid:string)=>apiRequest<any[]>(`/media/sessions/${sid}/recordings`),
  startRecording:(sid:string)=>apiRequest<any>(`/media/sessions/${sid}/recordings`,{method:'POST'}),
  recording:(id:string)=>apiRequest<any>(`/media/recordings/${id}`),
  setState:(id:string,status:string)=>apiRequest<any>(`/media/recordings/${id}/state`,{method:'POST',body:JSON.stringify({status})}),
  setRetention:(id:string,days:number)=>apiRequest<any>(`/media/recordings/${id}/retention`,{method:'POST',body:JSON.stringify({days})}),
  exportRecording:(id:string,destination:string)=>apiRequest<any>(`/media/recordings/${id}/export`,{method:'POST',body:JSON.stringify({destination})}),
  access:(id:string)=>apiRequest<any>(`/media/recordings/${id}/access`,{method:'POST'}),
  retentionWarnings:()=>apiRequest<any>('/media/admin/recordings/retention-warnings',{method:'POST'}),
  translation:(trackId:string)=>apiRequest<any>(`/media/translations/${trackId}`),
  setTranslationState:(trackId:string,status:string)=>apiRequest<any>(`/media/translations/${trackId}/state`,{method:'POST',body:JSON.stringify({status})}),
  studentShares:(id:string)=>apiRequest<any[]>(`/media/student/recordings/${id}/shares`),
};

export const credentialsApi = {
  types:()=>apiRequest<any[]>('/credentials/types'),
  createType:(b:Record<string,unknown>)=>apiRequest<any>('/credentials/types',{method:'POST',body:JSON.stringify(b)}),
  signers:()=>apiRequest<any[]>('/credentials/signers'),
  addSigner:(b:Record<string,unknown>)=>apiRequest<any>('/credentials/signers',{method:'POST',body:JSON.stringify(b)}),
  review:(rid:string,decision:string,note:string)=>apiRequest<any>(`/credentials/requests/${rid}/reviews`,{method:'POST',body:JSON.stringify({decision,note})}),
  issue:(rid:string,signerId:string)=>apiRequest<any>(`/credentials/requests/${rid}/issue`,{method:'POST',body:JSON.stringify({signer_id:signerId})}),
  revoke:(iid:string,reason:string)=>apiRequest<any>(`/credentials/issued/${iid}/revoke`,{method:'POST',body:JSON.stringify({reason})}),
  verify:(serial:string)=>apiRequest<any>(`/credentials/verify/${serial}`),
};

export const incubationApi = {
  centers:()=>apiRequest<any[]>('/incubation/centers'),
  createCenter:(b:Record<string,unknown>)=>apiRequest<any>('/incubation/centers',{method:'POST',body:JSON.stringify(b)}),
  cases:(centerId:string)=>apiRequest<any[]>(`/incubation/centers/${centerId}/cases`),
  createCase:(centerId:string,b:Record<string,unknown>)=>apiRequest<any>(`/incubation/centers/${centerId}/cases`,{method:'POST',body:JSON.stringify(b)}),
  addActivity:(caseId:string,b:Record<string,unknown>)=>apiRequest<any>(`/incubation/cases/${caseId}/activities`,{method:'POST',body:JSON.stringify(b)}),
  addReferral:(caseId:string,b:Record<string,unknown>)=>apiRequest<any>(`/incubation/cases/${caseId}/referrals`,{method:'POST',body:JSON.stringify(b)}),
  services:()=>apiRequest<any[]>('/incubation/external-services'),
  createService:(b:Record<string,unknown>)=>apiRequest<any>('/incubation/external-services',{method:'POST',body:JSON.stringify(b)}),
};

export const liveAdminApi = {
  overview:()=>apiRequest<any>('/live'),
  rooms:()=>apiRequest<any[]>('/live/rooms'),
  createRoom:(b:Record<string,unknown>)=>apiRequest<any>('/live/rooms',{method:'POST',body:JSON.stringify(b)}),
  room:(id:string)=>apiRequest<any>(`/live/rooms/${id}`),
  sessions:(roomId:string)=>apiRequest<any[]>(`/live/rooms/${roomId}/sessions`),
  createSession:(roomId:string)=>apiRequest<any>(`/live/rooms/${roomId}/sessions`,{method:'POST',body:JSON.stringify({})}),
  session:(id:string)=>apiRequest<any>(`/live/sessions/${id}`),
  setState:(id:string,status:string)=>apiRequest<any>(`/live/sessions/${id}/state`,{method:'POST',body:JSON.stringify({status})}),
  presenters:(sid:string)=>apiRequest<any[]>(`/classroom/sessions/${sid}/presenters`),
  turns:(sid:string)=>apiRequest<any[]>(`/classroom/sessions/${sid}/turns`),
  moveSeat:(pid:string,seat:string)=>apiRequest<any>(`/classroom/presenters/${pid}/seat`,{method:'POST',body:JSON.stringify({seat})}),
};

export const marApi = {
  summary:()=>apiRequest<any>('/mar/corpus/summary'),
  importCorpus:()=>apiRequest<any>('/mar/corpus/import',{method:'POST'}),
  fields:()=>apiRequest<any[]>('/mar/fields'),
  createField:(b:Record<string,unknown>)=>apiRequest<any>('/mar/fields',{method:'POST',body:JSON.stringify(b)}),
  subfields:(fieldId:string,b:Record<string,unknown>)=>apiRequest<any>(`/mar/fields/${fieldId}/subfields`,{method:'POST',body:JSON.stringify(b)}),
  degrees:()=>apiRequest<any[]>('/mar/degrees'),
  createDegree:(b:Record<string,unknown>)=>apiRequest<any>('/mar/degrees',{method:'POST',body:JSON.stringify(b)}),
};

export const commerceApi = {
  products:()=>apiRequest<any[]>('/commerce/products'),
  createProduct:(b:Record<string,unknown>)=>apiRequest<any>('/commerce/products',{method:'POST',body:JSON.stringify(b)}),
  prices:()=>apiRequest<any[]>('/commerce/prices'),
  createPrice:(b:Record<string,unknown>)=>apiRequest<any>('/commerce/prices',{method:'POST',body:JSON.stringify(b)}),
  quote:(b:Record<string,unknown>)=>apiRequest<any>('/commerce/quotes',{method:'POST',body:JSON.stringify(b)}),
  checkout:(quoteId:string)=>apiRequest<any>('/commerce/checkout',{method:'POST',body:JSON.stringify({quote_id:quoteId})}),
  entitlements:()=>apiRequest<any[]>('/commerce/entitlements'),
};

export const financeApi = {
  ledger:()=>apiRequest<any[]>('/finance/ledger'),
  splits:()=>apiRequest<any[]>('/finance/splits'),
  setSplit:(b:Record<string,unknown>)=>apiRequest<any>('/finance/splits',{method:'POST',body:JSON.stringify(b)}),
  payment:(b:Record<string,unknown>)=>apiRequest<any>('/finance/payments',{method:'POST',body:JSON.stringify(b)}),
  refund:(b:Record<string,unknown>)=>apiRequest<any>('/finance/refunds',{method:'POST',body:JSON.stringify(b)}),
  reconcile:(txId:string)=>apiRequest<any>(`/finance/reconcile/${txId}`),
  creditWallet:(userId:string,b:Record<string,unknown>)=>apiRequest<any>(`/wallet/admin/users/${userId}/credit`,{method:'POST',body:JSON.stringify(b)}),
};

export const aiAdminApi = {
  ask:(query:string,task='qa')=>apiRequest<any>('/ai/ask',{method:'POST',body:JSON.stringify({query,task})}),
  signals:()=>apiRequest<any[]>('/ai/support-signals'),
  createSignal:(b:Record<string,unknown>)=>apiRequest<any>('/ai/support-signals',{method:'POST',body:JSON.stringify(b)}),
  learnerSignals:(id:string)=>apiRequest<any[]>(`/ai/learners/${id}/support-signals`),
  indexArtifact:(id:string)=>apiRequest<any>(`/ai/rag/artifacts/${id}/index`,{method:'POST'}),
  candidates:()=>apiRequest<any[]>('/banges/candidates'),
  createCandidate:(b:Record<string,unknown>)=>apiRequest<any>('/banges/candidates',{method:'POST',body:JSON.stringify(b)}),
  reviewCandidate:(id:string,decision:string,note:string)=>apiRequest<any>(`/banges/candidates/${id}/review`,{method:'POST',body:JSON.stringify({decision,note})}),
  activateCandidate:(id:string)=>apiRequest<any>(`/banges/candidates/${id}/activate`,{method:'POST',body:JSON.stringify({})}),
};

export const offeringsApi = {
  terms:(iid:string)=>apiRequest<any[]>(`/institutions/${iid}/terms`),
  offerings:(iid:string)=>apiRequest<any[]>(`/institutions/${iid}/offerings`),
  createOffering:(iid:string,b:Record<string,unknown>)=>apiRequest<any>(`/institutions/${iid}/offerings`,{method:'POST',body:JSON.stringify(b)}),
  examPeriods:(iid:string)=>apiRequest<any[]>(`/institutions/${iid}/exam-periods`),
  enrollments:(oid:string)=>apiRequest<any[]>(`/offerings/${oid}/enrollments`),
  enroll:(oid:string,studentId:string)=>apiRequest<any>(`/offerings/${oid}/enrollments`,{method:'POST',body:JSON.stringify({student_id:studentId})}),
  staff:(oid:string)=>apiRequest<any[]>(`/offerings/${oid}/staff`),
  addStaff:(oid:string,b:Record<string,unknown>)=>apiRequest<any>(`/offerings/${oid}/staff`,{method:'POST',body:JSON.stringify(b)}),
  assessments:(oid:string)=>apiRequest<any[]>(`/offerings/${oid}/assessments`),
  createAssessment:(oid:string,b:Record<string,unknown>)=>apiRequest<any>(`/offerings/${oid}/assessments`,{method:'POST',body:JSON.stringify(b)}),
  grade:(aid:string,b:Record<string,unknown>)=>apiRequest<any>(`/assessments/${aid}/grade`,{method:'POST',body:JSON.stringify(b)}),
  aiGenerateExam:(oid:string,b:Record<string,unknown>)=>apiRequest<any>(`/offerings/${oid}/exams/ai-generate`,{method:'POST',body:JSON.stringify(b)}),
  questionBank:(b:Record<string,unknown>)=>apiRequest<any>('/exam-question-bank',{method:'POST',body:JSON.stringify(b)}),
  publishExam:(id:string)=>apiRequest<any>(`/exams/${id}/publish`,{method:'POST'}),
};

export const adminUsersApi = {
  createTenant:(name:string)=>apiRequest<any>('/tenants',{method:'POST',body:JSON.stringify({name})}),
  users:(tenantId:string)=>apiRequest<any[]>(`/tenants/${tenantId}/users`),
  createUser:(b:Record<string,unknown>)=>apiRequest<any>('/users',{method:'POST',body:JSON.stringify(b)}),
  revokeSessions:(userId:string)=>apiRequest<any>(`/users/${userId}/revoke-sessions`,{method:'POST'}),
};

export const auditAlertsApi = {
  alerts:(subscriptionId:string)=>apiRequest<any[]>(`/audit-courses/${subscriptionId}/alerts`,{method:'POST'}),
  due:()=>apiRequest<any[]>('/audit-courses/alerts/due'),
  acknowledge:(alertId:string)=>apiRequest<any>(`/audit-courses/alerts/${alertId}/acknowledge`,{method:'POST'}),
};

export const examAuthoringApi = {
  questions:(examId:string)=>apiRequest<any[]>(`/exams/${examId}/questions`),
  examPeriods:(syllabusId:string)=>apiRequest<any[]>(`/syllabi/${syllabusId}/exam-periods`),
  nodeReferences:(nodeId:string)=>apiRequest<any[]>(`/syllabus-nodes/${nodeId}/references`),
  publishArtifact:(artifactId:string)=>apiRequest<any>(`/content-artifacts/${artifactId}/publish`,{method:'POST'}),
  reviewIdentityCheck:(checkId:string,b:Record<string,unknown>)=>apiRequest<any>(`/exam-identity/checks/${checkId}/review`,{method:'POST',body:JSON.stringify(b)}),
  recommendations:(enrollmentId:string)=>apiRequest<any[]>(`/banges/enrollments/${enrollmentId}/recommendations`),
  incubationCases:()=>apiRequest<any[]>('/incubation/cases'),
  stageImport:(b:Record<string,unknown>)=>apiRequest<any>('/mar/imports/stage',{method:'POST',body:JSON.stringify(b)}),
};

export const systemApi = {
  build:()=>apiRequest<any>('/system/build'),
  capacity:()=>apiRequest<any>('/system/capacity'),
  readiness:()=>apiRequest<any>('/system/production-readiness'),
  approvePayout:(id:string)=>apiRequest<any>(`/payments/payouts/${id}/approve`,{method:'POST'}),
};

export const bookstoreApi = {
  prices:()=>apiRequest<BookPrice[]>('/bookstore/prices'),
  setPrices:(body:Record<string,unknown>)=>apiRequest<BookPrice[]>('/bookstore/prices',{method:'PUT',body:JSON.stringify(body)}),
  order:(body:Record<string,unknown>)=>apiRequest<BookOrder&{kdp_queue_id:string|null}>('/bookstore/orders',{method:'POST',body:JSON.stringify(body)}),
  myOrders:()=>apiRequest<BookOrder[]>('/bookstore/my-orders'),
};

export const salesApi = {
  orders:()=>apiRequest<SalesOrder[]>('/sales/orders'),
  seats:()=>apiRequest<Array<{user_id:string;granted_by:string|null}>>('/sales/seats'),
  grantSeat:(userId:string)=>apiRequest<unknown>('/sales/seats',{method:'POST',body:JSON.stringify({user_id:userId})}),
  revokeSeat:(userId:string)=>apiRequest<unknown>(`/sales/seats/${userId}`,{method:'DELETE'}),
  assign:(orderId:string)=>apiRequest<SalesOrder>(`/sales/orders/${orderId}/assign`,{method:'POST',body:JSON.stringify({})}),
  setStatus:(orderId:string,status:string)=>apiRequest<SalesOrder>(`/sales/orders/${orderId}/status`,{method:'POST',body:JSON.stringify({status})}),
  audit:(orderId:string)=>apiRequest<Array<{action:string;actor_user_id:string;detail:string;created_at:string}>>(`/sales/orders/${orderId}/audit`),
};

export const paymentsApi = {
  providers:()=>apiRequest<PaymentProvider[]>('/payments/providers'),
  registerProvider:(body:Record<string,unknown>)=>apiRequest<PaymentProvider>('/payments/providers',{method:'POST',body:JSON.stringify(body)}),
  createIntent:(body:Record<string,unknown>)=>apiRequest<PaymentIntent>('/payments/intents',{method:'POST',body:JSON.stringify(body)}),
  intent:(id:string)=>apiRequest<PaymentIntent>(`/payments/intents/${id}`),
  payouts:()=>apiRequest<any[]>('/payments/payouts'),
};

export const handsApi = {
  raise:(sessionId:string,topic:string)=>apiRequest<HandRaise>(`/classroom/sessions/${sessionId}/hands`,{method:'POST',body:JSON.stringify({topic})}),
  list:(sessionId:string,pending=false)=>apiRequest<HandRaise[]>(`/classroom/sessions/${sessionId}/hands${pending?'?pending=true':''}`),
  accept:(handId:string)=>apiRequest<HandRaise>(`/classroom/hands/${handId}/accept`,{method:'POST'}),
  dismiss:(handId:string)=>apiRequest<HandRaise>(`/classroom/hands/${handId}/dismiss`,{method:'POST'}),
  start:(handId:string)=>apiRequest<HandRaise>(`/classroom/hands/${handId}/start`,{method:'POST'}),
  end:(handId:string)=>apiRequest<HandRaise>(`/classroom/hands/${handId}/end`,{method:'POST'}),
  withdraw:(handId:string)=>apiRequest<HandRaise>(`/classroom/hands/${handId}/withdraw`,{method:'POST'}),
};

export type StudyPlanCourse = { id:string;course_version_id:string;course_title?:string;status:string;score:number|null;amount_minor:number;currency:string };
export type StudyPlanProgress = { plan_id:string;status:string;total_courses:number;passed_courses:number;remaining_courses:number;eligible_for_credential:boolean;issued_credential_id:string|null;total_cost_minor:number;currency:string };
export type AccreditationFile = { id:string;institution_id:string;requested_regime:string;status:string;ai_notes?:string };

export const pathsApi = {
  createPlan:(programVersionId:string)=>apiRequest<{id:string}>('/study-plans',{method:'POST',body:JSON.stringify({program_version_id:programVersionId})}),
  courses:(planId:string)=>apiRequest<StudyPlanCourse[]>(`/study-plans/${planId}/courses`),
  progress:(planId:string)=>apiRequest<StudyPlanProgress>(`/study-plans/${planId}/progress`),
  complete:(planId:string,courseVersionId:string,score:number)=>apiRequest<unknown>(`/study-plans/${planId}/complete`,{method:'POST',body:JSON.stringify({course_version_id:courseVersionId,score})}),
};

export const accreditationApi = {
  declare:(body:Record<string,unknown>)=>apiRequest<any>('/accreditation/declare',{method:'POST',body:JSON.stringify(body)}),
  addDocument:(fileId:string,body:Record<string,unknown>)=>apiRequest<any>(`/accreditation/files/${fileId}/documents`,{method:'POST',body:JSON.stringify(body)}),
  aiCheck:(fileId:string)=>apiRequest<any>(`/accreditation/files/${fileId}/ai-check`,{method:'POST'}),
  review:(fileId:string,approve:boolean,notes:string)=>apiRequest<any>(`/accreditation/files/${fileId}/review`,{method:'POST',body:JSON.stringify({approve,notes})}),
  pending:()=>apiRequest<AccreditationFile[]>('/accreditation/pending'),
};

export const auditApi = {
  subscribe:(body:Record<string,unknown>)=>apiRequest<any>('/audit-courses',{method:'POST',body:JSON.stringify(body)}),
  slots:(id:string)=>apiRequest<any[]>(`/audit-courses/${id}/slots`),
  dueAlerts:()=>apiRequest<any[]>('/audit-courses/alerts/due'),
};

export const laboratoryApi = {
  list:()=>apiRequest<Laboratory[]>('/laboratories'),
  mySessions:()=>apiRequest<LabSession[]>('/my/lab-sessions'),
  start:(versionId:string)=>apiRequest<LabSession>(`/laboratories/${versionId}/sessions`,{method:'POST'}),
  detail:(sessionId:string)=>apiRequest<LabSession>(`/lab-sessions/${sessionId}`),
  action:(sessionId:string,action:string,parameters:Record<string,unknown>)=>apiRequest<{state:Record<string,unknown>}>(`/lab-sessions/${sessionId}/actions`,{method:'POST',body:JSON.stringify({action,parameters})}),
  measure:(sessionId:string,name:string,value:number,unit:string)=>apiRequest<{id:string;name:string;value:number;unit:string}>(`/lab-sessions/${sessionId}/measurements`,{method:'POST',body:JSON.stringify({name,value,unit})}),
  checkpoint:(sessionId:string,name:string)=>apiRequest<{checkpoint:string}>(`/lab-sessions/${sessionId}/checkpoint`,{method:'POST',body:JSON.stringify({name})}),
  report:(sessionId:string,content:string)=>apiRequest<{id:string;status:string}>(`/lab-sessions/${sessionId}/report`,{method:'POST',body:JSON.stringify({content})}),
  complete:(sessionId:string)=>apiRequest<{id:string;status:string;completed_at:string}>(`/lab-sessions/${sessionId}/complete`,{method:'POST'}),
};

export const mediaApi = {
  recordings:()=>apiRequest<Recording[]>('/media/student/recordings'),
  retain:(recordingId:string,months:number)=>apiRequest<{recording_id:string;retention_paid_until:string;charge_minor:number;currency:string}>(`/media/student/recordings/${recordingId}/retention`,{method:'POST',body:JSON.stringify({months})}),
  shareInternal:(recordingId:string,recipientId:string)=>apiRequest<{id:string;kind:string;recipient_id:string;charge_minor:number}>(`/media/student/recordings/${recordingId}/share-internal`,{method:'POST',body:JSON.stringify({recipient_id:recipientId})}),
  shareLink:(recordingId:string)=>apiRequest<{id:string;token:string;charge_minor:number;access_charge_minor:number}>(`/media/student/recordings/${recordingId}/share-link`,{method:'POST'}),
  exportRecording:(recordingId:string,destination:string)=>apiRequest<{id:string;recording_id:string;destination:string;charge_minor:number}>(`/media/student/recordings/${recordingId}/export`,{method:'POST',body:JSON.stringify({destination})}),
};

export type Institution = {id:string;code:string;name:string;institution_type:string;status:string};
export type LearningOffering = {id:string;institution_id:string;term_id:string|null;code:string;title:string;kind:string;delivery_mode:string;resource_type:string;resource_id:string;price_minor:number;currency:string;capacity:number|null;independent_access:boolean;status:string};
export const institutionApi = {
 institutions:()=>apiRequest<Institution[]>('/institutions'),
 createInstitution:(v:{code:string;name:string;institution_type:string})=>apiRequest<Institution>('/institutions',{method:'POST',body:JSON.stringify(v)}),
 levels:(id:string)=>apiRequest<Array<{id:string;level_code:string;enabled:boolean}>>(`/institutions/${id}/levels`),
 setLevel:(id:string,level_code:string)=>apiRequest(`/institutions/${id}/levels`,{method:'POST',body:JSON.stringify({level_code,enabled:true})}),
 offerings:()=>apiRequest<LearningOffering[]>('/learning-offerings'),
 createOffering:(id:string,v:Record<string,unknown>)=>apiRequest<LearningOffering>(`/institutions/${id}/learning-offerings`,{method:'POST',body:JSON.stringify(v)}),
 addSchedule:(id:string,v:Record<string,unknown>)=>apiRequest(`/learning-offerings/${id}/schedules`,{method:'POST',body:JSON.stringify(v)}),
 schedules:(id:string)=>apiRequest<Array<Record<string,unknown>>>(`/learning-offerings/${id}/schedules`),
};


export type ExamSummary={id:string;title:string;exam_type:string;duration_minutes:number;max_attempts:number;max_score:number;period_title:string;starts_at:string;ends_at:string;available:boolean;attempts_used:number};
export type ExamQuestion={id:string;position:number;question_type:string;prompt:string;choices:string[];points:number;answer:string};
export type ExamAttempt={id:string;exam_id:string;title:string;status:string;attempt_no:number;started_at:string;submitted_at:string|null;score:number|null;percentage:number|null;questions:ExamQuestion[]};
export const examApi={
 myExams:()=>apiRequest<ExamSummary[]>('/my/exams'),
 start:(examId:string)=>apiRequest<ExamAttempt>(`/exams/${examId}/attempts`,{method:'POST'}),
 attempt:(id:string)=>apiRequest<ExamAttempt>(`/exam-attempts/${id}`),
 answer:(id:string,question_id:string,answer_text:string)=>apiRequest(`/exam-attempts/${id}/answers`,{method:'PUT',body:JSON.stringify({question_id,answer_text})}),
 submit:(id:string)=>apiRequest<{id:string;status:string;score:number;percentage:number;submitted_at:string}>(`/exam-attempts/${id}/submit`,{method:'POST'}),
};

export type ExamIdentityProfile={id:string;mode:string;voice_available:boolean;alternative_method:string|null;status:string};
export type ExamIdentityCheck={id:string;exam_id:string;mode:string;status:string;reason_code:string|null;face_score:number|null;voice_score:number|null;liveness_score:number|null};
export const examIdentityApi={
 profile:()=>apiRequest<ExamIdentityProfile|null>('/exam-identity/profile'),
 saveProfile:(v:{mode:string;face_template_ref?:string;voice_template_ref?:string;voice_available:boolean;alternative_method?:string})=>apiRequest<ExamIdentityProfile>('/exam-identity/profile',{method:'PUT',body:JSON.stringify(v)}),
 verify:(v:{exam_id:string;face_capture_ref?:string;voice_capture_ref?:string;liveness_capture_ref?:string})=>apiRequest<ExamIdentityCheck>('/exam-identity/checks',{method:'POST',body:JSON.stringify(v)}),
 event:(attemptId:string,event_type:string,details:Record<string,unknown>={})=>apiRequest(`/exam-attempts/${attemptId}/integrity-events`,{method:'POST',body:JSON.stringify({event_type,severity:'WARN',details})}),
};
